//method to check if Object is empty
import Cookies from 'js-cookie';
import { randomBytes } from 'crypto';
import moment from 'moment';
import { CN_FLEXIBLE_FORMAT_YEAR_MONTH, TEST_HOSTNAME } from './constants';

export const isEmpty = (value?: object): boolean => (value ? Object.keys(value).length === 0 : true);

export const canUseDOM = !!(typeof window !== 'undefined' && window.document);

export enum AuthStateEnum {
  UN_AUTHENTICATED = 'unauthenticated',
  REMEMBERED = 'remembered',
  AUTHENTICATED = 'authenticated',
  ATTEMPTED_SIGNIN = 'attemptedSignIn',
  DATALAYER_AUTHENTICATION_CHECK_VALUE = 'mr_prof_authentication_state',
}

/**
 * convertToCommaSeparatedNumber
 *
 * the function takes the number and converts the number into comma separated number and returns the number as a string.
 *
 * @param number - The number that has to be formatted to a localized number format.
 * @param locale - The string : locale for specific region.
 * @returns localized number format of the number that is passed based on the locale param.
 *  */
export function convertToCommaSeparatedNumber(number: number, locale = 'en-US'): string {
  return new Intl.NumberFormat(locale).format(number);
}

export function atGetCookie(cname: string) {
  const name = cname + '=',
    decodedCookie = decodeURIComponent(document.cookie),
    cookeArray = decodedCookie.split(';');
  for (let i = 0; i < cookeArray.length; i++) {
    let cookie = cookeArray[i];
    while (cookie.charAt(0) === ' ') {
      cookie = cookie.substring(1);
    }
    if (cookie.indexOf(name) === 0) {
      return cookie.substring(name.length, cookie.length);
    }
  }
  return '';
}

// Check if AEM Preview mode then check for wcmmode query parameter and return flag
export const getWCMModeFlagValue = () => {
  if (typeof window !== 'undefined') {
    const pathName = window?.location?.href;
    const paramWcmMode = pathName.includes('?') ? pathName.split('?') : '';
    const isWCM = paramWcmMode.includes('wcmmode=disabled');
    return isWCM;
  } else {
    return false;
  }
};

/**
 * Validates a string as non-empty
 *
 * @param str Value to be checked for truthiness, string type, and non-empty
 * @returns boolean
 */
export const isNonEmptyString = (str: unknown): boolean => {
  return !!str && typeof str == 'string' && str.replace(/\s+/g, '').length > 0;
};

export const generateRandomNumber = () => {
  if (window && window?.crypto) {
    const array = new Uint32Array(1);
    window.crypto.getRandomValues(array);
    return array[0] / 0xffffffff;
  } else {
    const randomBytesBuffer = randomBytes(4);
    // Convert to a float between 0 and 1
    return randomBytesBuffer.readUInt32LE(0) / 0xffffffff;
  }
};

/**
 * Formats a month and year into a Chinese month title string using the
 * specified format `CN_FLEXIBLE_FORMAT_YEAR_MONTH`.
 *
 * @param {Object} params - The parameters object containing the month and year values.
 * @param {number} [params.month] - The month to be formatted. This is zero-indexed (0 = January, 11 = December).
 * @param {number} [params.year] - The year to be formatted.
 *
 * @returns {string} - The formatted month and year string in Chinese format.
 *
 * @example
 * // Returns a string like "2023年 8月" if the format is 'YYYY年 M月'
 * const formattedTitle = formatMonthTitleCN({ month: 7, year: 2023 });
 */
export const formatMonthTitleCN = ({ month, year }: { month?: number; year?: number }): string => {
  return moment({ month, year }).format(CN_FLEXIBLE_FORMAT_YEAR_MONTH);
};

/**
 * Function to replace any json key (oldKey) with another key (newKey)
 * @param obj - model
 * @param oldKey - appliedCssClassName
 * @param newKey - styleClass
 * @returns
 */
export const replaceKeyInJson = (obj: object, oldKey: string, newKey: string): object => {
  if (obj && typeof obj === 'object') {
    if (Array.isArray(obj)) {
      return obj.map(item => replaceKeyInJson(item, oldKey, newKey));
    }

    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [
        key === oldKey ? newKey : key,
        replaceKeyInJson(value, oldKey, newKey),
      ])
    );
  }
  return obj; // Return non-object values as-is
};

/**
 * Repeatedly checks a condition until it returns true or a maximum number of attempts is reached.
 *
 * @param condition - A function that returns a boolean. The polling will stop when this returns true.
 * @param interval - The interval in milliseconds between checks. Default is 100ms.
 * @param maxAttempts - The maximum number of attempts before giving up. Default is 50.
 * @returns A Promise that resolves when the condition returns true, or rejects if the condition is not met within the allowed attempts.
 *
 * @example
 * // Wait for a DOM element to appear
 * await waitForCondition(() => !!document.querySelector('#my-element'), 200, 30);
 */
export const waitForCondition = async (condition: () => boolean, interval = 100, maxAttempts = 50): Promise<void> => {
  let attempts = 0;
  return new Promise((resolve, reject) => {
    const check = () => {
      if (condition()) {
        resolve();
      } else if (attempts < maxAttempts) {
        attempts++;
        setTimeout(check, interval);
      } else {
        reject(new Error('Condition not met within maxAttempts'));
      }
    };
    check();
  });
};

export const isMetaInAppBrowser = (): boolean => {
  if (!canUseDOM) return false;
  const userAgent = navigator.userAgent || '';
  return /FBAV|FBAN|FB_IAB/i.test(userAgent) || /Instagram/i.test(userAgent);
};

export const isCobrandBLPPath = (pathOrUrl: string): boolean => {
  try {
    const base = canUseDOM ? window.location.origin : 'https://marriott.com';
    const url = new URL(pathOrUrl, base);
    return url.pathname.toLowerCase().includes('/credit-cards/chase-credit-cards');
  } catch {
    return (pathOrUrl || '').toLowerCase().includes('/credit-cards/chase-credit-cards');
  }
};

// method to clear certain keys from session storage and cookies
export const clearSessionAndCookie = () => {
  sessionStorage.removeItem('UserDetailsGlobalStore');
  sessionStorage.removeItem('AccountPersistentState');
  sessionStorage.removeItem('bannerMessagesState');
  sessionStorage.removeItem('EnrollmentMessageState');
  sessionStorage.removeItem('mr_enrolled');
  sessionStorage.removeItem('pointsTransferState');
  sessionStorage.removeItem('masked_phone');
  sessionStorage.removeItem('mfa_option');
  sessionStorage.removeItem('remember_me');
  sessionStorage.removeItem('encrypted_data');
  sessionStorage.removeItem('encrypted_verify_data');
  sessionStorage.removeItem('customer_email');
  sessionStorage.removeItem('tracking_page_name');
  Cookies?.remove('MarketingConsent');
  Cookies?.remove('EmeaMarketingConsent');
};

export interface DataObject {
  redirect: boolean;
  nextStateURI: string;
}

export async function fetchTimeoutRedirectUrl(endpoint: string): Promise<DataObject | unknown> {
  let url = endpoint;
  let credentialType: 'same-origin' | 'include' = 'same-origin';
  let returnTo = window.location.href;
  if (process.env['NODE_ENV'] === 'development') {
    url = `${TEST_HOSTNAME}${url}`;
    credentialType = 'include';
    returnTo = `${TEST_HOSTNAME}/default.mi`;
  }
  try {
    const body = JSON.stringify({ returnTo });
    const config: RequestInit = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body,
      credentials: credentialType,
    };
    const response = await window.fetch(url, config);
    const data = await response.json();
    return data;
  } catch (error) {
    // ignore
  }
  return {};
}

const findNthOccurence = (str: string, nth: number, char: string): number => {
  let index = 0;
  for (let i = 0; i < nth; i += 1) {
    if (index !== -1) {
      index = str.indexOf(char, index + 1);
    }
  }
  return index;
};

export const addLocalePrefix = (sourceUrl: string, targetUrl: string): string => {
  const localeString = sourceUrl?.substring(1, findNthOccurence(sourceUrl, 1, '/')) || '';
  if (localeString.length === 2 || (localeString.length === 5 && localeString.charAt(2) === '-')) {
    return localeString.toLowerCase() !== 'en-us' &&
      targetUrl?.startsWith('/') &&
      targetUrl?.substring(1, findNthOccurence(targetUrl, 1, '/'))?.toLowerCase() !== localeString?.toLowerCase()
      ? '/' + localeString + targetUrl
      : targetUrl;
  } else {
    return targetUrl;
  }
};

export function removeSessionStorage(key: string): void {
  window.sessionStorage?.removeItem(key);
}

export const clearLoggedInSessionData = (): void => {
  removeSessionStorage('UserDetailsGlobalStore');
  removeSessionStorage('AccountPersistentState');
  removeSessionStorage('bannerMessagesState');
  removeSessionStorage('EnrollmentMessageState');
  removeSessionStorage('mr_enrolled');
  removeSessionStorage('pointsTransferState');
  removeSessionStorage('masked_phone');
  removeSessionStorage('remember_me');
  removeSessionStorage('encrypted_data');
  removeSessionStorage('encrypted_verify_data');
  removeSessionStorage('customer_email');
  removeSessionStorage('tracking_page_name');
};
/**
 * Helper function to check if a hostname matches a trusted domain.
 * Prevents phishing attacks by ensuring exact match or proper subdomain.
 *
 * @param hostname - The hostname to check (e.g., 'www.marriott.com')
 * @param trustedDomain - The trusted domain to match against (e.g., 'marriott.com')
 * @returns true if hostname matches the trusted domain exactly or is a proper subdomain
 *
 * @example
 * ```tsx
 * isHostnameTrusted('www.marriott.com', 'marriott.com'); // true
 * isHostnameTrusted('marriott.com', 'marriott.com'); // true
 * isHostnameTrusted('marriott.com.evil.com', 'marriott.com'); // false (phishing)
 * isHostnameTrusted('fakemarriott.com', 'marriott.com'); // false
 * ```
 */
export const isHostnameTrusted = (hostname: string, trustedDomain: string): boolean => {
  const normalizedHost = hostname.toLowerCase();
  const normalizedDomain = trustedDomain.toLowerCase();
  // Exact match or proper subdomain (must end with .trustedDomain)
  return normalizedHost === normalizedDomain || normalizedHost.endsWith('.' + normalizedDomain);
};

/**
 * Sanitizes a URL to prevent XSS and phishing attacks by blocking dangerous protocols
 * and optionally validating against a whitelist of allowed domains.
 * This function should be used whenever setting href attributes from user-controlled input.
 *
 * @param url - The URL to sanitize
 * @param options - Optional configuration
 * @param options.allowedDomains - Array of allowed domains/patterns. If provided, only URLs matching these domains are allowed
 * @returns Sanitized URL or '#' if the URL is potentially dangerous
 *
 * @example
 * ```tsx
 * // Basic usage - blocks XSS via dangerous protocols
 * sanitizeUrl('javascript:alert("XSS")'); // returns '#'
 * sanitizeUrl('https://marriott.com'); // returns 'https://marriott.com'
 *
 * // With domain whitelist - also blocks phishing
 * sanitizeUrl('https://evil.com', {
 *   allowedDomains: ['marriott.com', 'creditcards.chase.com']
 * }); // returns '#'
 * ```
 */
export const sanitizeUrl = (url: string, options?: { allowedDomains?: string[] }): string => {
  if (!url || typeof url !== 'string') {
    return '#';
  }

  const trimmedUrl = url.trim();

  // Allow relative URLs (return as-is since they're safe - they navigate within the same origin)
  if (trimmedUrl.startsWith('/') || trimmedUrl.startsWith('./') || trimmedUrl.startsWith('../')) {
    return trimmedUrl;
  }

  try {
    // For absolute URLs, parse without a base (will throw if not absolute, which is what we want)
    const urlObj = new URL(trimmedUrl);
    const protocol = urlObj.protocol.toLowerCase();

    // Only allow safe protocols
    const allowedProtocols = ['http:', 'https:', 'mailto:', 'tel:'];
    if (!allowedProtocols.includes(protocol)) {
      console.warn(`Blocked potentially dangerous URL protocol: ${protocol}`);
      return '#';
    }

    // If domain whitelist is provided, validate the hostname (protects against phishing)
    if (options?.allowedDomains && options.allowedDomains.length > 0) {
      const hostname = urlObj.hostname.toLowerCase();
      const isAllowed = options.allowedDomains.some(domain => isHostnameTrusted(hostname, domain));

      if (!isAllowed) {
        console.warn(`Blocked URL with untrusted domain: ${hostname}`);
        return '#';
      }
    }

    return urlObj.href;
  } catch (error) {
    // Invalid URL format - return safe fallback
    console.error('Error sanitizing URL:', error);
    return '#';
  }
};
